import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNotificationSettings, getPreferences, updatePreferences } from '../../../store/Notification/index';

// Helper: read a preference value with backward compat (old boolean → { inApp, email })
const getChannels = (pref, field) => {
  if (!pref) return { inApp: true, email: false };
  const val = pref[field];
  if (val && typeof val === 'object') return { inApp: val.inApp !== false, email: val.email === true };
  if (typeof val === 'boolean') return { inApp: val, email: false };
  return { inApp: true, email: false };
};

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const { categoryConfig, preferences } = useSelector(state => state['Notification']);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [localPrefs, setLocalPrefs] = useState([]);

  useEffect(() => {
    dispatch(getNotificationSettings());
    dispatch(getPreferences());
  }, []);

  useEffect(() => {
    if (preferences && preferences.length > 0) {
      setLocalPrefs(JSON.parse(JSON.stringify(preferences)));
    }
  }, [preferences]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const handleToggle = (category, field, channel) => {
    const updated = localPrefs.map(p => {
      if (p.category === category) {
        const current = getChannels(p, field);
        return { ...p, [field]: { ...current, [channel]: !current[channel] } };
      }
      return p;
    });
    setLocalPrefs(updated);
    dispatch(updatePreferences({ preferences: updated }));
  };

  const getEnabledCount = (config) => {
    const pref = localPrefs.find(p => p.category === config.category);
    if (!pref) return config.toggles.length;
    return config.toggles.filter(t => {
      const ch = getChannels(pref, t.field);
      return ch.inApp || ch.email;
    }).length;
  };

  return (
    <div className='notification-settings'>
      <div className='notification-settings-header'>
        <h3>What you get notified about</h3>
        <p>Choose what topics matter to you and how you get notified about them.</p>
      </div>
      <div className='notification-categories'>
        {categoryConfig.map(config => {
          const pref = localPrefs.find(p => p.category === config.category);
          const isExpanded = expandedCategories.includes(config.category);
          const hasToggles = !config.alwaysOn && config.toggles.length > 0;

          return (
            <div key={config.category} className='notification-category-row'>
              <div
                className={`notification-category-header ${isExpanded ? 'expanded' : ''}`}
                onClick={() => hasToggles && toggleCategory(config.category)}
              >
                <div className='notification-category-left'>
                  {hasToggles && (
                    <span className='notification-category-chevron'>›</span>
                  )}
                  <span className='notification-category-label'>{config.label}</span>
                  {config.alwaysOn && <span className='notification-always-on-badge'>Always on</span>}
                </div>
                <div className='notification-category-right'>
                  {hasToggles && !isExpanded && (
                    <span className={`notification-category-count ${getEnabledCount(config) === 0 ? 'all-off' : ''}`}>
                      {getEnabledCount(config) === config.toggles.length
                        ? 'All on'
                        : getEnabledCount(config) === 0
                          ? 'All off'
                          : `${getEnabledCount(config)} of ${config.toggles.length} on`
                      }
                    </span>
                  )}
                  {isExpanded && hasToggles && (
                    <div className='notification-channel-headers'>
                      <span className='notification-channel-header'>In-App</span>
                      <span className='notification-channel-header'>Email</span>
                    </div>
                  )}
                </div>
              </div>
              {isExpanded && hasToggles && (
                <div className='notification-category-items'>
                  {config.toggles.map(toggle => {
                    const ch = getChannels(pref, toggle.field);
                    return (
                      <div key={toggle.field} className='notification-toggle-item'>
                        <div className='notification-toggle-info'>
                          <span className='notification-toggle-label'>{toggle.label}</span>
                          {toggle.description && (
                            <span className='notification-toggle-desc'>{toggle.description}</span>
                          )}
                        </div>
                        <div className='notification-channel-checkboxes'>
                          <label className='notification-checkbox'>
                            <input
                              type='checkbox'
                              checked={ch.inApp}
                              onChange={() => handleToggle(config.category, toggle.field, 'inApp')}
                            />
                            <span className='notification-checkmark'></span>
                          </label>
                          <label className='notification-checkbox'>
                            <input
                              type='checkbox'
                              checked={ch.email}
                              onChange={() => handleToggle(config.category, toggle.field, 'email')}
                            />
                            <span className='notification-checkmark'></span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationSettings;
